import React, { useMemo, useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Label,
} from 'recharts';
import { Legend } from '../Layout';
import { useReport } from '../ReportContext';
import { useChartFormatting, useTimeGrouping, useTransformData, useSeries } from '../hooks';
import { doesItemMatchSeriesKey } from '../hooks/useDynamicSeriesUtils';
import { 
  useChartStyles, 
  ChartTooltip, 
  useDrilldownHandler,
  getComponentType,
  getStackId,
  getAxisConfiguration,
  useMonthReferenceLines,
  renderCustomDot,
  renderActiveDot,
} from './utils';
import { addDays, differenceInMonths, startOfDay, startOfMonth, endOfDay } from 'date-fns';

// Custom label for the hover reference line
const HoverReferenceLineLabel = ({ viewBox, value, labelFormatter }) => {
  const { x, y } = viewBox;
  // Basic positioning, might need refinement based on chart layout/axis position
  return (
    <text x={x} y={y} dy={-10} fill="#333" fontSize="12" textAnchor="middle">
      {labelFormatter(value)}
    </text>
  );
};

export const CartesianChart = ({ data: chartData, config }) => {
  const { classes } = useChartStyles();
  const handleElementClick = useDrilldownHandler(config);
  const [hoveredTimestamp, setHoveredTimestamp] = useState(null);
  const { isDrilldownEnabled } = useReport();

  const transformedData = useTransformData(chartData);
  const { series: baseSeries, showLegend, generateDynamicSeriesWithData } = useSeries(config);
  
  // Generate dynamic series using the transformed data
  const series = useMemo(() => {
    // Check if we have any series with dynamic configuration
    const hasDynamicConfig = config?.series?.some?.(s => s.dynamicSeries) || 
                            (Array.isArray(config?.series) && config.series.some(s => s.dynamicSeries));
    
    // Check if this is a report that uses its own series function (like CompletedVsOutstandingReport)
    // or has its own drilldown configuration
    const isCustomSeriesFunction = typeof config?.series === 'function';
    const hasDrilldownConfig = !!config?.drilldown;
    
    // Only apply dynamic series generation for configs with dynamicSeries property
    // and avoid it for reports with custom series functions or drilldown config
    if (hasDynamicConfig && transformedData?.length > 0 && !isCustomSeriesFunction && !hasDrilldownConfig) {
      return generateDynamicSeriesWithData(transformedData);
    }
    
    return baseSeries;
  }, [baseSeries, config, transformedData, generateDynamicSeriesWithData]);
  
  const { timeGrouping } = useTimeGrouping(transformedData);
  const { selectedChartType } = useReport();
  
  // Determine if it's a date axis early
  const isDateAxis = config?.axisConfig?.categoryAxis?.type === 'date' || config?.axisConfig?.categoryAxis?.type === 'time';
  
  // Pass isDateAxis to useChartFormatting and get renderTick
  const { renderTick, formatTooltipDate, MultilineYAxisTick } = useChartFormatting(timeGrouping, isDateAxis);

  const axisConfig = config.axisConfig || {};
  const isHorizontalChart = selectedChartType?.includes('horizontal');
  const chartOrientation = isHorizontalChart ? 'horizontal' : 'vertical';
  const rechartsLayout = isHorizontalChart ? 'vertical' : 'horizontal';

  const dataKeyForAxis = config?.axisConfig?.categoryAxis?.dataKey || (isDateAxis ? 'date' : 'label');

  // Calculate ticks based on date range if it's a date axis
  const dynamicTicks = useMemo(() => {
    if (!isDateAxis || !transformedData || transformedData.length === 0) {
      return undefined; // Not a date axis or no data
    }

    const timestamps = transformedData
      .map(d => d[dataKeyForAxis])
      .filter(d => typeof d === 'number' && d > 0)
      .sort((a, b) => a - b);

    if (timestamps.length === 0) return undefined;

    const minTimestamp = timestamps[0];
    const maxTimestamp = timestamps[timestamps.length - 1];
    const minDate = startOfDay(new Date(minTimestamp));
    const maxDate = endOfDay(new Date(maxTimestamp)); // Use end of day for comparison

    const durationMonths = differenceInMonths(maxDate, minDate);

    let currentTickDate = minDate;
    const ticks = [];

    // Restore the duration-based tick generation logic
    if (durationMonths <= 2) { // Daily ticks
      while (currentTickDate <= maxDate) {
        ticks.push(currentTickDate.getTime());
        currentTickDate = addDays(currentTickDate, 1);
      }
    } else if (durationMonths <= 6) { // Every 3 days
      while (currentTickDate <= maxDate) {
        ticks.push(currentTickDate.getTime());
        currentTickDate = addDays(currentTickDate, 3);
      }
    } else { // Monthly ticks (start of month)
      currentTickDate = startOfMonth(minDate);
      while (currentTickDate <= maxDate) {
        // Only add if it's within the actual min/max range or the first tick
         if (currentTickDate.getTime() >= minTimestamp || ticks.length === 0) {
           // Check if month start is later than the actual max data point
           if (currentTickDate.getTime() <= maxTimestamp || 
              (ticks.length > 0 && ticks[ticks.length - 1] < maxTimestamp)) {
             ticks.push(currentTickDate.getTime());
           }
         }
        // Move to the next month
        currentTickDate = startOfMonth(addDays(currentTickDate, 32)); // Go to next month reliably
      }
       // Ensure the last data point's month start is included if needed and not already there
      const lastDataMonthStart = startOfMonth(new Date(maxTimestamp)).getTime();
      if (ticks.length > 0 && ticks[ticks.length - 1] < lastDataMonthStart && lastDataMonthStart <= maxTimestamp) {
          ticks.push(lastDataMonthStart);
      }
    }
    
    // Sort and remove duplicates just in case (primarily for the monthly logic)
    return [...new Set(ticks.sort((a, b) => a - b))];

  }, [transformedData, isDateAxis, dataKeyForAxis]);

  // Get base axis configuration
  const baseCategoryAxisProps = getAxisConfiguration(
    config,
    transformedData,
    chartOrientation,
    timeGrouping,
    renderTick
  );

  // Combine base props with dynamic ticks if available
  const categoryAxisProps = useMemo(() => {
    return {
      ...baseCategoryAxisProps,
      ...(isDateAxis && dynamicTicks && { 
          ticks: dynamicTicks, 
          interval: 0,
          height: baseCategoryAxisProps.height || 100,
          tickLine: baseCategoryAxisProps.tickLine ?? { stroke: '#999', strokeWidth: 1 },
          axisLine: baseCategoryAxisProps.axisLine ?? { stroke: '#333', strokeWidth: 1 },
          tickSize: baseCategoryAxisProps.tickSize ?? 8,
          minTickGap: baseCategoryAxisProps.minTickGap ?? -200,
      }),
      // Add settings for horizontal charts to enable text wrapping
      ...(isHorizontalChart && {
        width: 180,
        // For horizontal charts, use the MultilineYAxisTick
        tick: MultilineYAxisTick,
        interval: 0,
        tickMargin: 5
      }),
      // For vertical charts, replace newlines with spaces
      ...(!isHorizontalChart && {
        tickFormatter: value => typeof value === 'string' ? value.replace(/\n/g, ' ') : value
      })
    };
  }, [baseCategoryAxisProps, isDateAxis, dynamicTicks, isHorizontalChart, MultilineYAxisTick]);
  
  const monthReferenceLines = useMonthReferenceLines(
    transformedData, 
    config, 
    categoryAxisProps.type
  );
  
  // Use alignment to determine axisId if not explicitly provided
  const valueAxesConfig = (axisConfig.valueAxes || [{ alignment: 'left' }]).map(axis => ({
    ...axis,
    // Ensure axisId is always set, defaulting first to alignment, then 'left'
    axisId: axis.axisId || axis.alignment || 'left'
  }));
  
  const adjustedValueAxesConfig = valueAxesConfig.map(axis => ({
    ...axis,
    // For horizontal charts, value axis appears at bottom
    // For vertical charts, use the alignment (left/right), defaulting to 'left'
    orientation: chartOrientation === 'horizontal' ? 'bottom' : (axis.alignment || 'left')
  }));

  const CategoryAxisComponent = chartOrientation === 'vertical' ? XAxis : YAxis;
  const ValueAxisComponent = chartOrientation === 'vertical' ? YAxis : XAxis;

  // Modified Tooltip component to update hover state
   const CustomTooltipWithHover = (props) => {
    const { active, payload } = props;

    React.useEffect(() => {
      if (active && payload && payload.length > 0) {
        const timestamp = payload[0].payload?.[dataKeyForAxis];
        if (typeof timestamp === 'number') {
          setHoveredTimestamp(timestamp);
        } else {
          setHoveredTimestamp(null); // Reset if dataKey is not found or not number
        }
      } else {
        setHoveredTimestamp(null); // Reset when tooltip is inactive
      }
       // Cleanup function to reset on unmount (optional but good practice)
       return () => setHoveredTimestamp(null); 
    }, [active, payload]); // Rerun effect when active or payload changes

    // Render the original tooltip content
    return (
    <ChartTooltip
      {...props}
      legendItems={series}
      timeGrouping={timeGrouping}
      chartType={selectedChartType}
      transformedData={transformedData}
      formatDateLabel={formatTooltipDate}
      useSimplifiedTooltip={config.useSimplifiedTooltip}
      allowCMEdit={!!config.allowCMEdit}
      tooltipCaptions={config.tooltipCaptions}
    />
  );
  };

  if (!transformedData || transformedData.length === 0) {
    return null;
  }

  const scrollingHeight = isHorizontalChart && transformedData.length > 10;

  return (
    <>
      <div 
        className={classes.chartContainer} 
        style={scrollingHeight ? { 
          height: 'auto',
          minHeight: '300px',
          alignItems: 'flex-start' // Align items at the top
        } : undefined}
      >
        <div style={{ 
          height: scrollingHeight ? `${Math.max(300, transformedData.length * 65)}px` : '100%', 
          flexGrow: 1,
          width: '100%',
          position: 'relative'
        }}>
          <ResponsiveContainer 
            width="100%" 
            height="100%"
          >
            <ComposedChart
              data={transformedData}
              layout={rechartsLayout}
              margin={{
                top: 10,
                right: 30,
                left: 30,
                bottom: chartOrientation === 'vertical' ? 
                  (isDateAxis ? 100 : (categoryAxisProps.angle ? 80 : (categoryAxisProps.height || 60) + 20)) : 
                  50,
              }}
              barCategoryGap={config.barCategoryGap ?? (transformedData.length > 30 ? '10%' : '20%')}
              barGap={config.barGap ?? (transformedData.length > 30 ? 2 : 4)}
            >
              <CartesianGrid
                strokeDasharray="3 3"
              />
              
              {/* Render standard month reference lines only if not using detailed date axis */}
              {!isDateAxis && categoryAxisProps.type === 'number' && monthReferenceLines.map((refLine, index) => (
                <ReferenceLine
                  key={`month-${index}`}
                  x={refLine.date}
                  yAxisId={chartOrientation === 'vertical' ? 'left' : undefined}
                  xAxisId={chartOrientation === 'horizontal' ? 'left' : undefined}
                  stroke="#ccc"
                  strokeDasharray="3 3"
                  label={{
                    value: refLine.label,
                    position: 'bottom',
                    fill: '#666',
                    fontSize: 12,
                    dy: 15
                  }}
                />
              ))}

              {/* Render Hover ReferenceLine */}
              {hoveredTimestamp !== null && isDateAxis && chartOrientation === 'vertical' && (
                 <ReferenceLine
                   key="hover-line"
                   x={hoveredTimestamp}
                   yAxisId="left"
                   stroke="rgba(0, 0, 0, 0.6)"
                   strokeDasharray="2 2"
                   ifOverflow="extendDomain"
                 >
                    <Label 
                      content={<HoverReferenceLineLabel labelFormatter={formatTooltipDate} />} 
                    />
                 </ReferenceLine>
              )}

              {hoveredTimestamp !== null && isDateAxis && chartOrientation === 'horizontal' && (
                 <ReferenceLine
                   key="hover-line-y"
                   y={hoveredTimestamp}
                   xAxisId="left"
                   stroke="rgba(0, 0, 0, 0.6)"
                   strokeDasharray="2 2"
                   ifOverflow="extendDomain"
                 >
                    {/* Label positioning might need adjustment for horizontal */}
                    {/* <Label content={<HoverReferenceLineLabel labelFormatter={formatTooltipDate} />} /> */}
                 </ReferenceLine>
              )}

              <CategoryAxisComponent
                {...categoryAxisProps}
              />

              {adjustedValueAxesConfig.map((axis) => (
                <ValueAxisComponent
                  key={axis.axisId}
                  yAxisId={chartOrientation === 'vertical' ? axis.axisId : undefined}
                  xAxisId={chartOrientation === 'horizontal' ? axis.axisId : undefined}
                  orientation={axis.orientation}
                  type="number"
                  allowDecimals={axis.allowDecimals ?? true}
                  domain={axis.domain || [0, 'auto']}
                  label={{
                    value: axis.label,
                    angle: chartOrientation === 'vertical' ? -90 : 0,
                    position: chartOrientation === 'vertical' 
                      ? (axis.orientation === 'right' ? 'insideRight' : 'insideLeft') 
                      : 'insideBottom',
                    offset: chartOrientation === 'vertical' ? undefined : -20,
                    style: { textAnchor: 'middle' }
                  }}
                  tickFormatter={axis.tickFormatter}
                  width={axis.width}
                />
              ))}

              <Tooltip
                content={<CustomTooltipWithHover />}
                cursor={{ fill: "rgba(206, 206, 206, 0.2)" }}
                isAnimationActive={false}
              />

              {series.map((item) => {
                // Filter data for dynamic series
                let seriesData = null; // Default to null (no override)
                if (item.keyField && item.keyValue) {
                  seriesData = transformedData.filter(d => doesItemMatchSeriesKey(d, item.keyField, item.keyValue));
                  
                  // If filtered data is empty, skip rendering this series
                  if (seriesData.length === 0) {
                    return null;
                  }
                }
                
                // For allZeroOrLess check, use appropriate data source
                const dataToCheck = seriesData || transformedData;
                
                // Check if all data points for this series are zero or less
                const allZeroOrLess = dataToCheck.every(d => (d[item.templateKey || item.key] ?? 0) <= 0);
                
                // If all values are zero or less, don't render the series component
                if (allZeroOrLess) {
                  return null; 
                }
                
                // For dynamic series, use the template key for data
                const dataKey = item.templateKey || item.key;
                
                const { key, ...restCommonProps } = {
                  key: item.key,
                  dataKey: dataKey,
                  name: item.label,
                  yAxisId: chartOrientation === 'vertical' ? (item.valueAxisId || (item.alignment || 'left')) : undefined,
                  xAxisId: chartOrientation === 'horizontal' ? (item.valueAxisId || (item.alignment || 'left')) : undefined,
                  isAnimationActive: false,
                  // Update click handler to only be active if drilldown is enabled
                  onClick: isDrilldownEnabled() ? (data, index) => handleElementClick(data, index.payload, item) : undefined,
                  className: isDrilldownEnabled() ? classes.clickableElement : '',
                  cursor: isDrilldownEnabled() ? 'pointer' : 'default',
                  // Only add data prop if we have filtered data
                  ...(seriesData && { data: seriesData })
                };

                const componentType = getComponentType(item, selectedChartType);

                switch (componentType) {
                  case 'line':
                    return (
                      <Line
                        {...restCommonProps}
                        dataKey={dataKey}
                        name={item.label}
                        yAxisId={chartOrientation === 'vertical' ? (item.valueAxisId || (item.alignment || 'left')) : undefined}
                        xAxisId={chartOrientation === 'horizontal' ? (item.valueAxisId || (item.alignment || 'left')) : undefined}
                        stroke={item.color || item.defaultColor}
                        strokeWidth={item.strokeWidth || 2}
                        strokeDasharray={item.strokeDasharray}
                        dot={item.dot === false ? false : (props) => renderCustomDot(
                          props,
                          item,
                          config,
                          isDrilldownEnabled() || config.onDataPointClick ? classes.clickableElement : '',
                          config.onDataPointClick
                        )}
                        activeDot={item.activeDot === false ? false : (props) => renderActiveDot(
                          props,
                          item,
                          // Add click handler for drilldown or CM data point editing
                          isDrilldownEnabled() ?
                            (e, data, series) => handleElementClick(data, e.payload, series) :
                            config.onDataPointClick ?
                              (e, data) => config.onDataPointClick(data) :
                              null,
                          isDrilldownEnabled() || config.onDataPointClick ? classes.clickableElement : ''
                        )}
                      />
                    );
                  case 'area':
                    return (
                      <Area
                        key={key}
                        {...restCommonProps}
                        type="monotone"
                        fill={item.color || item.defaultColor}
                        fillOpacity={item.fillOpacity || 0.6}
                        stroke={item.color || item.defaultColor}
                        strokeWidth={item.strokeWidth || 2}
                      />
                    );
                  case 'bar':
                  default:
                    return (
                      <Bar
                        dataKey={dataKey}
                        {...restCommonProps}
                        maxBarSize={config.maxBarSize ?? 45}
                        stackId={getStackId(item, selectedChartType)}
                        barSize={config.barSize ?? Math.max(35, Math.min(45, 1000 / Math.max(1, transformedData.length)))}
                      >
                        {(seriesData || transformedData)?.map((entry, index) => (
                          <Cell
                            key={`cell-${item.key}-${index}`}
                            fill={item.color || item.getColorByIndex(index)}
                          />
                        ))}
                      </Bar>
                    );
                }
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {showLegend() && (
          <div style={{ 
            minWidth: '140px',
            maxWidth: '240px', 
            flexShrink: 0, 
            height: '100%', 
            display: 'flex',
            alignItems: 'flex-start',
            paddingLeft: '10px'
          }}>
            <Legend 
              items={series.filter(item => {
                // For dynamic series, always show (their labels are asset names)
                if (item.keyField && item.keyValue) return true;

                // For static min/max series, hide if all values are null/undefined
                if (item.key === 'minValue' || item.key === 'maxValue') {
                  const dataKey = item.templateKey || item.key;
                  const dataToCheck = transformedData || [];
                  const hasAnyValue = dataToCheck.some(d => d[dataKey] != null);
                  return hasAnyValue;
                }

                return true;
              })}
              chartType={selectedChartType}
              position="right" 
            />
          </div>
        )}
      </div>
    </>
  );
};
